import { FormControl, FormControlLabel, Switch as MuiSwitch } from '@mui/material';
import React, { forwardRef } from 'react';
import { CommonDataProps, SxProps } from '../../utils';

type NativeButtonProps = React.ComponentPropsWithoutRef<'button'>;

export interface SwitchProps
  extends Omit<NativeButtonProps, 'color' | 'value' | 'onChange' | 'children'>,
    CommonDataProps {
  /**
   * If `true`, the component is checked.
   */
  checked?: boolean;
  /**
   * A text or an element to be used in an enclosing label element.
   */
  label?: React.ReactNode;
  /**
   * The position of the label.
   * @default 'end'
   */
  labelPlacement?: 'end' | 'start' | undefined;
  /**
   * Name attribute of the `input` element.
   */
  name?: string;
  /**
   * Callback fired when the state is changed.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   * You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  /**
   * The value of the component. The DOM API casts this to a string.
   * The browser uses "on" as the default value.
   */
  value?: unknown;
}

export const Switch = forwardRef(function Switch(
  { 'aria-label': ariaLabel, label, labelPlacement, ...props }: SwitchProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  if (label !== undefined) {
    return (
      <FormControl component="fieldset">
        <FormControlLabel control={<MuiSwitch {...props} ref={ref} />} label={label} labelPlacement={labelPlacement} />
      </FormControl>
    );
  }

  return <MuiSwitch {...props} ref={ref} inputProps={{ 'aria-label': ariaLabel }} />;
});
